/**
 * Holding-pattern entry logic.
 *
 * This matches the working HTML version.
 * Do not replace this with the earlier -70/+110 classifier.
 */

export type Turn = "right" | "left";

export type Entry = "direct" | "teardrop" | "parallel";

/**
 * Normalize an angle to (-180, 180].
 */
export function normalizeAngle(angleDeg: number): number {
  let r = ((angleDeg % 360) + 360) % 360;

  if (r > 180) {
    r -= 360;
  }

  return r;
}

/**
 * Normalize a heading or course for input/display.
 */
export function normalizeCourse(angleDeg: number): number {
  if (!Number.isFinite(angleDeg)) {
    return 0;
  }

  const rounded = Math.round(angleDeg);

  if (rounded === 360) {
    return 360;
  }

  return ((rounded % 360) + 360) % 360;
}

/**
 * Aircraft heading relative to inbound course.
 */
export function relativeAngle(
  headingDeg: number,
  inboundCourseDeg: number,
): number {
  return normalizeAngle(headingDeg - inboundCourseDeg);
}

/**
 * Classify holding-pattern entry.
 *
 * This intentionally mirrors the working HTML:
 *
 * Right turns:
 *   Direct:   0° <= s < 180°
 *   Parallel: -110° <= s < 0°
 *   Teardrop: remaining sector
 *
 * Left turns mirror the same sectors.
 */
export function classifyEntry(
  headingDeg: number,
  inboundCourseDeg: number,
  turn: Turn = "right",
): Entry {
  const r = relativeAngle(headingDeg, inboundCourseDeg);
  const s = turn === "right" ? r : -r;

  if (s >= -70 && s < 110) {
    return "direct";
  }

  if (s >= -180 && s < -70) {
    return "parallel";
  }

  return "teardrop";
}

export function entrySectorWidth(entry: Entry): number {
  if (entry === "direct") {
    return 180;
  }

  if (entry === "teardrop") {
    return 70;
  }

  return 110;
}

export function formatCourse(courseDeg: number): string {
  const normalized = normalizeCourse(courseDeg);
  const display = normalized === 0 ? 360 : normalized;

  return display.toString().padStart(3, "0");
}

export function explainEntry(entry: Entry, turn: Turn): string {
  const side = turn === "right" ? "right" : "left";
  const opposite = turn === "right" ? "left" : "right";

  if (entry === "direct") {
    return `Fly straight to the fix, then turn ${side} onto the outbound leg and fly the hold.`;
  }

  if (entry === "teardrop") {
    return `Cross the fix, fly the outbound leg about 30° toward the holding side, then turn ${side} to intercept inbound and fly to the fix.`;
  }

  return `Cross the fix, parallel the outbound course on the non-holding side, then turn ${opposite} through more than 180° to come back and intercept inbound.`;
}

/**
 * Outbound (reciprocal) course for a given inbound holding course.
 */
export function outboundCourse(inboundCourseDeg: number): number {
  return normalizeCourse(inboundCourseDeg + 180);
}

/**
 * Teardrop outbound track: the outbound course offset 30 degrees toward the
 * holding side. Matches the track drawn by the visualizer.
 */
export function teardropTrack(inboundCourseDeg: number, turn: Turn): number {
  const sign = turn === "right" ? 1 : -1;
  return normalizeCourse(inboundCourseDeg + 180 - 30 * sign);
}

function turnWord(turn: Turn): string {
  return turn === "right" ? "Right" : "Left";
}

function oppositeTurnWord(turn: Turn): string {
  return turn === "right" ? "Left" : "Right";
}

/**
 * Steady-state hold, as a numbered step list.
 *
 * No wind is modeled, so heading equals course and the outbound leg is flown
 * for the same time as the inbound leg.
 */
export function holdInstructions(
  inboundCourseDeg: number,
  turn: Turn,
): string[] {
  const inbound = formatCourse(inboundCourseDeg);
  const outbound = formatCourse(outboundCourse(inboundCourseDeg));
  const side = turnWord(turn);

  return [
    `Fly to the fix on CRS ${inbound} with HDG ${inbound}.`,
    `Turn ${side} to HDG ${outbound} outbound.`,
    `Fly HDG ${outbound} outbound for the same time as the inbound leg (about one minute).`,
    `Turn ${side} and intercept CRS ${inbound} inbound.`,
    `Repeat.`,
  ];
}

/**
 * Entry maneuver, as a numbered step list, ending with a handoff into
 * holdInstructions. No wind is modeled, so heading equals course.
 */
export function entryInstructions(
  entry: Entry,
  inboundCourseDeg: number,
  headingDeg: number,
  turn: Turn,
): string[] {
  const inbound = formatCourse(inboundCourseDeg);
  const outbound = formatCourse(outboundCourse(inboundCourseDeg));
  const teardrop = formatCourse(teardropTrack(inboundCourseDeg, turn));
  const side = turnWord(turn);
  const opposite = oppositeTurnWord(turn);

  void headingDeg;

  if (entry === "direct") {
    return [
      `Fly to the fix and cross it.`,
      `Go to Hold Instructions: Step 2.`,
    ];
  }

  if (entry === "teardrop") {
    return [
      `Fly to the fix and cross it.`,
      `Turn ${side} to HDG ${teardrop} and fly the same time as the inbound leg (about one minute).`,
      `Turn ${side} and intercept the inbound CRS ${inbound}.`,
      `Fly to the fix on CRS ${inbound} with HDG ${inbound}.`,
      `Go to Hold Instructions: Step 2.`,
    ];
  }

  return [
    `Fly to the fix and cross it.`,
    `Turn to HDG ${outbound} to parallel the outbound course on the non-holding side. Fly about one minute.`,
    `Turn ${opposite} through more than 180° and intercept the inbound CRS ${inbound}.`,
    `Fly to the fix on CRS ${inbound} with HDG ${inbound}.`,
    `Go to Hold Instructions: Step 2.`,
  ];
}